import "@/App.css";
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Phone, MessageCircle, User, Flame, Eye, Calendar as CalIcon, Star,
  ShieldCheck, Volume2, UtensilsCrossed, Sparkles, BadgeCheck,
  MapPin, Users, ChevronDown, Search, ArrowRight, Heart,
  Clock, CreditCard, Headphones, Zap, Lock, Plus, Truck,
  Instagram, Facebook, Youtube, RotateCcw, RefreshCw, Tag,
  Menu, X, Sofa, Ticket
} from "lucide-react";
import { openWhatsApp, DEFAULT_GREETING } from "./lib/whatsapp";

const HOME_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-6 py-3 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_20px_rgba(212,175,116,0.25)] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] ${className}`}>
    {children}
  </div>
);

const Logo = () => (
  <a
    href="/"
    className="flex items-center"
    data-testid="cinevibe-logo"
  >
    <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
      <img
        src="/images/logo.png"
        alt="CineVibe logo"
        className="w-full h-full object-cover"
      />
    </div>
  </a>
);

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = [
    { label: "HOME", href: "/" },
    { label: "BOOK SCREENING", href: "/book" },
    { label: "ORDER FOOD", href: "/food" },
    { label: "EXPERIENCES", href: "/experiences" },
    { label: "LOCATIONS", href: "/locations" },
    { label: "FRANCHISE", href: "/franchise" },
    { label: "ABOUT US", href: "/about" },
  ];
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
      <Section className="flex items-center justify-between py-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          {items.map((it, i) => (
            <a key={it.label} href={it.href} data-testid={`nav-${it.label.toLowerCase().replace(/ /g, "-")}`}
              className={`text-[12px] tracking-[0.16em] transition-colors ${i === 0 ? "text-[#f4d089]" : "text-[#d8cdb3] hover:text-[#f4d089]"}`}>
              {it.label}{i === 0 && <span className="block h-[2px] mt-2 bg-[#f4d089]" />}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3 md:gap-4">
          <button data-testid="header-whatsapp" onClick={() => openWhatsApp(DEFAULT_GREETING)} className="hidden md:flex items-center gap-2 text-[#7ad99c] text-sm hover:text-[#a3eebd]">
            <MessageCircle size={18} /> WhatsApp
          </button>
          <button data-testid="header-login" onClick={() => (window.location.href = "/account")} className="hidden md:flex items-center gap-2 text-[#d8cdb3] text-sm hover:text-[#f4d089]">
            <User size={18} /> Login
          </button>
          <GoldButton data-testid="header-book-now" onClick={() => (window.location.href = "/book")} className="text-xs sm:text-sm tracking-[0.15em] !px-3 sm:!px-5">BOOK NOW</GoldButton>
          {/* Hamburger — visible below lg */}
          <button
            data-testid="header-mobile-toggle"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#d4a574]/30 text-[#f4d089] hover:bg-[#1a0f06]"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </Section>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#d4a574]/15 bg-[#0a0604]/95 backdrop-blur-md">
          <Section className="py-4 flex flex-col gap-1">
            {items.map((it, i) => (
              <a
                key={it.label}
                href={it.href}
                data-testid={`mnav-${it.label.toLowerCase().replace(/ /g, "-")}`}
                onClick={() => setMobileOpen(false)}
                className={`py-3 px-2 text-[13px] tracking-[0.16em] border-b border-[#3a2a14]/40 ${i === 0 ? "text-[#f4d089]" : "text-[#d8cdb3] hover:text-[#f4d089]"}`}
              >
                {it.label}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-4">
              <button
                onClick={() => { setMobileOpen(false); openWhatsApp(DEFAULT_GREETING); }}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md border border-[#7ad99c]/40 text-[#7ad99c] text-sm"
                data-testid="mnav-whatsapp"
              >
                <MessageCircle size={18} /> WhatsApp
              </button>
              <button
                onClick={() => { setMobileOpen(false); window.location.href = "/account"; }}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md border border-[#d4a574]/30 text-[#d8cdb3] text-sm"
                data-testid="mnav-login"
              >
                <User size={18} /> Login
              </button>
            </div>
          </Section>
        </div>
      )}
    </header>
  );
};

const StatusStrip = () => {
  const [c, setC] = useState(null);
  useEffect(() => {
    axios.get(`${HOME_API}/content/home`).then((r) => setC(r.data)).catch(() => setC({}));
  }, []);
  const stats = [
    { icon: <Flame size={16} className="text-[#ff8a4c]" />,  text: c?.strip_slots    || "Only 3 slots left for today in Delhi" },
    { icon: <Eye   size={16} className="text-[#7ad99c]" />,  text: c?.strip_viewers  || "9 people viewing now" },
    { icon: <CalIcon size={16} className="text-[#f4d089]" />,text: c?.strip_bookings || "27 bookings today" },
    { icon: <Star  size={16} className="text-[#f4d089] fill-[#f4d089]" />, text: c?.strip_rating || "Rated 4.8/5 by 1,200+ guests" },
  ];
  return (
    <Section className="pt-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#3a2a14]/40 border border-[#3a2a14]/60 rounded-md overflow-hidden">
        {stats.map((s, i) => (
          <div key={i} className="bg-[#0d0905] py-2.5 px-4 flex items-center justify-center gap-2 text-[12.5px] text-[#c9bfa6]">
            {s.icon}<span>{s.text}</span>
          </div>
        ))}
      </div>
    </Section>
  );
};

const Hero = () => {
  const features = [
    { i: <MapPin size={18} />, t: "100% Private" },
    { i: <Volume2 size={18} />, t: "Premium", s: "Sound & Screen" },
    { i: <UtensilsCrossed size={18} />, t: "In-room", s: "Dining" },
    { i: <Sparkles size={18} />, t: "Custom", s: "Decor & Add-ons" },
    { i: <Sofa size={18} />, t: "Comfortable", s: "Recliner Seating" },
    { i: <BadgeCheck size={18} />, t: "Instant", s: "Confirmation" },
  ];
  return (
    <Section className="pt-8 lg:pt-10">
      <div className="grid lg:grid-cols-[1fr_1.05fr] gap-8 lg:gap-10 items-center">
        <div className="order-2 lg:order-1">
          <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Your Movie.<br />Your Space.<br /><span className="text-[#f4d089]">Your Vibe.</span>
          </h1>
          <p className="mt-6 text-[#bfb393] text-base sm:text-lg max-w-md leading-relaxed">
            Private screenings | Premium experience | <br />Unforgettable memories.
          </p>
          <div className="mt-10 grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
            {features.map((f, i) => (
              <div key={i} className="flex flex-col items-start gap-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[#d4a574]/40 bg-[#1a0f06] flex items-center justify-center text-[#f4d089]">{f.i}</div>
                <div className="text-[11px] leading-tight text-[#d8cdb3]"><div>{f.t}</div>{f.s && <div>{f.s}</div>}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="order-1 lg:order-2 relative rounded-xl overflow-hidden border border-[#d4a574]/20 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
          <img
  src="/images/hero.jpg"
  alt="luxury private cinema"
  className="w-full h-auto aspect-[4/3] sm:aspect-[16/10] lg:h-[480px] lg:aspect-auto object-cover object-center"
/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0604] via-transparent to-transparent" />
          <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
  <img
    src="/images/logo.png"
    alt="CineVibe logo"
    className="w-full h-full object-cover"
  />
</div>
        </div>
      </div>
    </Section>
  );
};

const Field = ({ label, icon, value, onChange, options, placeholder, testid, disabled }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] uppercase tracking-[0.14em] text-[#a89878]">{label}</label>
    <div className={`relative flex items-center gap-2 bg-[#0a0604] border rounded-md px-3 py-2.5 transition ${
      disabled ? "border-[#3a2a14]/40 opacity-60" : "border-[#3a2a14]/80 hover:border-[#d4a574]/50 focus-within:border-[#d4a574]"
    }`}>
      <span className="text-[#d4a574]">{icon}</span>
      <select
        data-testid={testid}
        disabled={disabled}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        className="appearance-none bg-transparent text-[#f4d089] text-sm flex-1 outline-none cursor-pointer pr-5 disabled:cursor-not-allowed"
      >
        <option value="" className="bg-[#0d0905] text-[#c9bfa6]">{placeholder}</option>
        {(options || []).map((o) => (
          <option key={o.value} value={o.value} className="bg-[#0d0905] text-white">{o.label}</option>
        ))}
      </select>
      <ChevronDown size={14} className="text-[#a89878] absolute right-3 pointer-events-none" />
    </div>
  </div>
);

const DateField = ({ label, icon, value, onChange, testid }) => {
  const min = new Date().toISOString().slice(0, 10);
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] uppercase tracking-[0.14em] text-[#a89878]">{label}</label>
      <div className="relative flex items-center gap-2 bg-[#0a0604] border border-[#3a2a14]/80 rounded-md px-3 py-2.5 hover:border-[#d4a574]/50 focus-within:border-[#d4a574] transition">
        <span className="text-[#d4a574]">{icon}</span>
        <input
          type="date"
          data-testid={testid}
          min={min}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="bg-transparent text-[#f4d089] text-sm flex-1 outline-none [color-scheme:dark]"
        />
      </div>
    </div>
  );
};

/** Map a free-text place name to one of our supported cities. */
const matchCity = (name = "") => {
  const s = name.toLowerCase();
  if (/(delhi|gurgaon|gurugram|noida|ghaziabad|faridabad|ncr)/.test(s)) return "Delhi NCR";
  if (/(mumbai|bombay|navi mumbai|thane)/.test(s)) return "Mumbai";
  if (/(bangalore|bengaluru)/.test(s)) return "Bangalore";
  if (/pune/.test(s)) return "Pune";
  if (/(hyderabad|secunderabad)/.test(s)) return "Hyderabad";
  return null;
};

/** Detect user city via browser geolocation + free OSM reverse geocoding. */
const detectUserCity = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`,
            { headers: { "Accept-Language": "en" } }
          );
          const data = await r.json();
          const a = data.address || {};
          const raw =
            a.city || a.town || a.village || a.suburb ||
            a.state_district || a.state || data.display_name || "";
          resolve({ matched: matchCity(raw) || "Delhi NCR", raw });
        } catch (e) { reject(e); }
      },
      (err) => reject(err),
      { timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  });

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;



const BookingBar = () => {
  const [cities, setCities] = useState([]);
const [branches, setBranches] = useState([]);
const [experiences, setExperiences] = useState([]);
const [experience, setExperience] = useState("");
const [cityId, setCityId] = useState("");
const [branchId, setBranchId] = useState("");
const [date, setDate] = useState("");
const [guests, setGuests] = useState("");
const [formErr, setFormErr] = useState("");

// Load cities + experiences once
useEffect(() => {
  axios.get(`${API}/cities`).then((r) => setCities(r.data || [])).catch(() => {});
  axios.get(`${API}/experiences`).then((r) => setExperiences(r.data || [])).catch(() => {});
}, []);

  // Load branches when city changes
  useEffect(() => {
    if (!cityId) { setBranches([]); setBranchId(""); return; }
    axios.get(`${API}/branches`, { params: { city_id: cityId } })
      .then((r) => setBranches(r.data || []))
      .catch(() => setBranches([]));
    setBranchId("");
  }, [cityId]);

  const detectedCityName = useMemo(
    () => cities.find((c) => c.id === cityId)?.name || "",
    [cities, cityId]
  );

  const cityOptions = cities.map((c) => ({ value: c.id, label: c.name }));
  const branchOptions = branches.map((b) => ({ value: b.id, label: b.name }));
  const experienceOptions = experiences.map((e) => ({ value: e.slug || e.id, label: e.name }));
  const guestOptions = Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1), label: `${i + 1} ${i === 0 ? "Guest" : "Guests"}`,
  }));

  const onSubmit = () => {
    setFormErr("");
    if (!cityId) return setFormErr("Please select a city.");
    if (!branchId) return setFormErr("Please select a location.");
    if (!date) return setFormErr("Please pick a date.");
    if (!guests) return setFormErr("Please choose number of guests.");
    const qs = new URLSearchParams({
      city_id: cityId, branch_id: branchId, date, guests,
      ...(experience ? { experience } : {}),
    });
    window.location.href = `/book?${qs.toString()}`;
  };

  return (
    <Section className="pt-8">
      <div className="rounded-xl border border-[#d4a574]/25 bg-[#0d0905] p-5 sm:p-6 shadow-[0_10px_40px_rgba(0,0,0,0.4)]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
         <Field label="Experience" icon={<User size={14} />}
            value={experience} onChange={setExperience}
            options={experienceOptions} placeholder="Select Experience" testid="bk-experience" />
          <Field label="City" icon={<MapPin size={14} />}
            value={cityId} onChange={setCityId}
            options={cityOptions}
            placeholder="Select City"
            testid="bk-city" />
          <Field label="Location" icon={<MapPin size={14} />}
            value={branchId} onChange={setBranchId}
            options={branchOptions}
            placeholder={cityId ? (branchOptions.length ? "Select Location" : "No locations yet") : "Select City first"}
            disabled={!cityId}
            testid="bk-location" />
          <DateField label="Date" icon={<CalIcon size={14} />}
            value={date} onChange={setDate} testid="bk-date" />
          <Field label="Guests" icon={<Users size={14} />}
            value={guests} onChange={setGuests}
            options={guestOptions} placeholder="Select Guests" testid="bk-guests" />
          <GoldButton data-testid="bk-check" onClick={onSubmit}
            className="h-[46px] flex items-center justify-center gap-2 tracking-[0.16em]">
            <Search size={16} /> CHECK AVAILABILITY
          </GoldButton>
        </div>

        {formErr && (
          <div className="mt-3 text-sm text-[#e8456a]" data-testid="bk-form-error">{formErr}</div>
        )}

        {detectedCityName && (
          <div className="mt-4 text-center text-sm text-[#bfb393]" data-testid="bk-detect-status">
            <span className="text-[#7ad99c]">📍 Showing near: <b>{detectedCityName}</b></span>
          </div>
        )}
      </div>
    </Section>
  );
};

const HowItWorks = () => {
  const steps = [
    { n: 1, i: <User size={20} />, t: "Choose Experience", s: "Pick the experience you love" },
    { n: 2, i: <MapPin size={20} />, t: "Select City & Location", s: "Find The CineVibe near you" },
    { n: 3, i: <CalIcon size={20} />, t: "Pick Date & Time", s: "Choose your preferred slot" },
    { n: 4, i: <Sparkles size={20} />, t: "Customize", s: "Add food, décor & special add-ons" },
    { n: 5, i: <CreditCard size={20} />, t: "Pay & Enjoy", s: "Make payment & enjoy your private show" },
    { n: 6, i: <Ticket size={20} />, t: "Confirm & Get Details", s: "Booking confirmation & venue details on WhatsApp" },
  ];
  return (
    <Section className="pt-20">
      <div className="flex items-center justify-center gap-4 mb-12">
        <span className="h-px w-16 bg-gradient-to-r from-transparent to-[#d4a574]/60" />
        <h2 className="font-display tracking-[0.3em] text-[#f4d089] text-lg">HOW IT WORKS</h2>
        <span className="h-px w-16 bg-gradient-to-l from-transparent to-[#d4a574]/60" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {steps.map((s) => (
          <div key={s.n} className="flex flex-col items-center text-center px-2">
            <div className="text-[#f4d089] text-sm mb-2">{s.n}</div>
            <div className="w-16 h-16 rounded-full border border-[#d4a574]/40 flex items-center justify-center text-[#f4d089] mb-3">{s.i}</div>
            <div className="text-white text-sm font-medium mb-1">{s.t}</div>
            <div className="text-xs text-[#a89878] leading-snug max-w-[140px]">{s.s}</div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const ExperienceCard = ({ img, title, sub, hrs, ppl, price, badge, slug }) => (
  <a
    href={`/experience/${slug}`}
    data-testid={`experience-card-${slug}`}
    className="block"
  >
    <Card className="overflow-hidden group cursor-pointer hover:border-[#d4a574]/50 transition">
      <div className="relative h-44 overflow-hidden">
        <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0604] via-[#0a0604]/30 to-transparent" />
        {badge && <span className="absolute top-3 right-3 bg-[#f4d089] text-[#1a0f06] text-[10px] font-bold tracking-wider px-2.5 py-1 rounded">{badge}</span>}
      </div>
      <div className="p-4">
        <h4 className="text-white font-display text-lg">{title}</h4>
        <p className="text-xs text-[#a89878] mt-1">{sub}</p>
      </div>
    </Card>
  </a>
);

const PackageRow = ({ img, title, price, slug }) => (
  <a
    href={`/package/${slug}`}
    data-testid={`package-row-${slug}`}
    className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#1a0f06] transition cursor-pointer"
  >
    <img src={img} alt={title} className="w-14 h-14 object-cover rounded-md border border-[#3a2a14]" />
    <div className="flex-1"><div className="text-white text-sm">{title}</div><div className="text-[#f4d089] text-xs">Starting at ₹{price}</div></div>
    <ArrowRight size={14} className="text-[#a89878]" />
  </a>
);

const Experiences = () => {
  const [exps, setExps] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/experiences`)
      .then((r) => setExps(r.data || []))
      .catch(() => setExps([]));
    axios.get(`${API}/packages`)
      .then((r) => setPkgs(r.data || []))
      .catch(() => setPkgs([]));
  }, []);

  const packs = pkgs.slice(0, 4).map((p) => ({
    slug: p.slug,
    img: p.image,
    title: p.name,
    price: p.base_price ? Number(p.base_price).toLocaleString("en-IN") : "—",
  }));

  const top4 = exps.slice(0, 4).map((e) => ({
    slug: e.slug,
    img: e.image,
    title: e.name,
    sub: e.sub || "",
    hrs: e.hrs || "3 HRS",
    ppl: e.ppl || "",
    price: e.base_price ? Number(e.base_price).toLocaleString("en-IN") : "—",
    badge: e.badge || "",
  }));

  return (
    <Section className="pt-20">
      <div className="grid lg:grid-cols-[2.4fr_1fr] gap-6">
        <div>
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[#f4d089] tracking-[0.18em] text-sm font-semibold">CHOOSE YOUR EXPERIENCE</h3>
            <a className="text-[#f4d089] text-xs flex items-center gap-1" href="/experiences" data-testid="experiences-view-all">View All <ArrowRight size={12} /></a>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {top4.length === 0
              ? <div className="col-span-2 lg:col-span-4 text-center text-xs text-[#a89878] py-10">No experiences yet. Add some from Super Admin → Experiences.</div>
              : top4.map((e) => <ExperienceCard key={e.slug || e.title} {...e} />)}
          </div>
        </div>
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#f4d089] tracking-[0.18em] text-sm font-semibold">READY PACKAGES</h3>
            <a className="text-[#f4d089] text-xs flex items-center gap-1" href="/packages" data-testid="packages-view-all">View All <ArrowRight size={12} /></a>
          </div>
          <div className="space-y-1">{packs.map((p) => <PackageRow key={p.title} {...p} />)}</div>
        </Card>
      </div>
    </Section>
  );
};

const AddOn = ({ img, title, price }) => (
  <div className="text-center group cursor-pointer">
    <div className="relative w-full aspect-square rounded-lg overflow-hidden border border-[#3a2a14]/70 bg-[#0a0604]">
      <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition" />
      <Heart size={14} className="absolute top-2 right-2 text-white/80" />
    </div>
    <div className="text-xs text-white mt-2">{title}</div><div className="text-[11px] text-[#f4d089]">₹{price}+</div>
  </div>
);

const FoodCard = ({ img, title, price }) => (
  <div className="text-center">
    <div className="aspect-square rounded-lg overflow-hidden border border-[#3a2a14]/70 bg-[#0a0604]">
      <img src={img} alt={title} className="w-full h-full object-cover" />
    </div>
    <div className="text-xs text-white mt-2">{title}</div>
  </div>
);

const AddonsAndFood = () => {
  const [addons, setAddons] = useState([]);
  const [home, setHome] = useState(null);

useEffect(() => {
  axios.get(`${HOME_API}/content/home`)
    .then((r) => setHome(r.data))
    .catch(() => setHome({}));
}, []);
  useEffect(() => {
    axios.get(`${API}/addons`).then((r) => setAddons(r.data || [])).catch(() => setAddons([]));
  }, []);
  const fallbackImg = "https://images.unsplash.com/photo-1518895949257-7621c3c786d7?auto=format&fit=crop&w=400&q=80";
  const displayAddons = addons.slice(0, 6).map((a) => ({
    img: a.image || fallbackImg,
    title: a.name,
    price: a.price != null ? Number(a.price).toLocaleString("en-IN") : "—",
  }));
  const food = home?.food_items?.length
  ? home.food_items
  : [
      {
        img: "https://images.unsplash.com/photo-1578849278619-e73505e9610f?auto=format&fit=crop&w=400&q=80",
        title: "Classic Popcorn",
        price: "149",
      },
      {
        img: "https://images.unsplash.com/photo-1582169296194-e4d644c48063?auto=format&fit=crop&w=400&q=80",
        title: "Cheesy Nachos",
        price: "249",
      },
      {
        img: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=400&q=80",
        title: "Peri Peri Burger",
        price: "279",
      },
      {
        img: "https://images.unsplash.com/photo-1517701550927-30cf4ba1dba5?auto=format&fit=crop&w=400&q=80",
        title: "Cold Coffee",
        price: "129",
      },
    ];
  return (
    <Section className="pt-16">
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[#f4d089] tracking-[0.16em] text-sm font-semibold">MAKE IT EXTRA SPECIAL</h3>
            <a className="text-[#f4d089] text-xs flex items-center gap-1" href="/addons" data-testid="home-view-all-addons">View All Add-ons <ArrowRight size={12} /></a>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="home-addons-grid">{displayAddons.length > 0 ? displayAddons.map((a) => <AddOn key={a.title} {...a} />) : <div className="col-span-3 sm:col-span-6 text-center text-xs text-[#a89878] py-6">No add-ons available yet.</div>}</div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[#f4d089] tracking-[0.16em] text-sm font-semibold">
  {home?.food_heading || "DELICIOUSLY CURATED FOOD"}
</h3>
            <a className="text-[#f4d089] text-xs flex items-center gap-1" href="/food" data-testid="home-view-full-menu">View Full Menu <ArrowRight size={12} /></a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{food.map((f) => <FoodCard key={f.title} {...f} />)}</div>
          <div className="mt-4 text-center text-[11px] text-[#a89878] flex items-center justify-center gap-2">
            <Truck size={12} className="text-[#7ad99c]" /> Delivered fresh inside your private room
          </div>
        </Card>
      </div>
    </Section>
  );
};

const StatsAndBilling = () => {
  const [c, setC] = useState(null);
  useEffect(() => {
    axios.get(`${HOME_API}/content/home`).then((r) => setC(r.data)).catch(() => setC({}));
  }, []);

  const icons = [
    <Star size={18} />, <Users size={18} />, <Lock size={18} />,
    <Headphones size={18} />, <Zap size={18} />, <ShieldCheck size={18} />,
  ];
  const defaultStats = [
    { v: "4.8/5", t: "Google Rating" },
    { v: "10,000+", t: "Celebrations Hosted" },
    { v: "100%", t: "Private Experience" },
    { v: "24/7", t: "Customer Support" },
    { v: "Instant", t: "Booking Confirmed" },
    { v: "Secure", t: "Payments" },
  ];
  const statsData = (c?.stats?.length ? c.stats : defaultStats).map((s, i) => ({
    ...s, i: icons[i] || <Star size={18} />,
  }));

  return (
    <Section className="pt-16">
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        <Card className="p-6">
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
            {statsData.map((s, idx) => (
              <div key={idx} className="text-center">
                <div className="w-10 h-10 mx-auto rounded-full border border-[#d4a574]/30 flex items-center justify-center text-[#f4d089] mb-2">{s.i}</div>
                <div className="text-white font-display text-lg">{s.v}</div>
                <div className="text-[10px] text-[#a89878] mt-0.5">{s.t}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-center text-[#f4d089] tracking-[0.18em] text-xs font-semibold mb-4">
            {c?.billing_heading || "TRANSPARENT BILLING"}
          </div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="border border-[#3a2a14] rounded-lg p-3">
              <div className="text-white text-sm">{c?.billing_card1_title || "SCREENING (GST 18%)"}</div>
              <div className="text-[10px] text-[#a89878] mt-1">{c?.billing_card1_sub || "Billed by The CineVibe"}</div>
              <div className="text-[10px] text-[#bfb393] mt-2">{c?.billing_card1_body || "Private Screening Charges"}</div>
            </div>
            <div className="border border-[#3a2a14] rounded-lg p-3">
              <div className="text-white text-sm">{c?.billing_card2_title || "FOOD (GST 5%)"}</div>
              <div className="text-[10px] text-[#a89878] mt-1">{c?.billing_card2_sub || "Billed via Café (Billing Software)"}</div>
              <div className="text-[10px] text-[#bfb393] mt-2">{c?.billing_card2_body || "Food & Beverages"}</div>
            </div>
          </div>
          <div className="text-center text-[10px] text-[#a89878] mt-3">
            {c?.billing_footer || "Two separate invoices for complete transparency"}
          </div>
        </Card>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm text-[#bfb393]">
        <div className="flex items-center gap-3">
          <MessageCircle size={18} className="text-[#7ad99c]" />
          {c?.trust1_l1 || "Booking confirmation"}<br />{c?.trust1_l2 || "on WhatsApp"}
        </div>
        <div className="flex items-center gap-3">
          <Volume2 size={18} className="text-[#f4d089]" />
          {c?.trust2_l1 || "Premium 4K screen &"}<br />{c?.trust2_l2 || "Dolby surround sound"}
        </div>
        <div className="flex items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <Lock size={18} className="text-[#7ad99c]" />
            {c?.trust3_l1 || "100% private theatre"}<br />{c?.trust3_l2 || "only for your group"}
          </div>
          <div className="bg-[#1a3a22] border border-[#7ad99c]/40 rounded-md px-3 py-2 text-center">
            <Tag size={14} className="text-[#7ad99c] inline mr-1" />
            <span className="text-[#7ad99c] text-[11px] font-bold">{c?.promo_code || "Use Code: CINE005"}</span>
            <div className="text-[10px] text-[#bfb393]">{c?.promo_label || "Get 5% instant OFF"}</div>
          </div>
        </div>
      </div>
    </Section>
  );
};

const LocationsRow = () => {
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [c, setC] = useState({});

  useEffect(() => {
    axios.get(`${API}/cities`).then((r) => setCities(r.data || [])).catch(() => {});
    axios.get(`${API}/branches`).then((r) => setBranches(r.data || [])).catch(() => {});
    axios.get(`${HOME_API}/content/home`).then((r) => setC(r.data || {})).catch(() => setC({}));
  }, []);

  // Build one card per city, showing the first branch in that city as the subtitle.
  const cards = useMemo(() => {
    return cities.map((c) => {
      const cityBranches = branches.filter((b) => b.city_id === c.id);
      const first = cityBranches[0];
      return {
        n: c.name,
        s: cityBranches.length > 1
            ? `${cityBranches.length} Outlets`
            : (first ? first.name : "Coming Soon"),
        img: first?.image_url || "/images/hero.jpg",
      };
    });
  }, [cities, branches]);

  return (
    <Section className="pt-16">
      <div className="grid lg:grid-cols-[1.6fr_1fr_1fr] gap-5">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#f4d089] tracking-[0.16em] text-sm font-semibold">OUR LOCATIONS</h3>
            <a className="text-[#f4d089] text-xs flex items-center gap-1" href="/locations" data-testid="home-view-all-locations">View All Locations <ArrowRight size={12} /></a>
          </div>
          {cards.length === 0 ? (
            <div className="text-center text-[#bfb393] text-sm py-6">Loading locations…</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {cards.map((c) => (
                <a key={c.n} href="/locations" className="text-center cursor-pointer group">
                  <div className="aspect-[4/5] rounded-md overflow-hidden border border-[#3a2a14] mb-2">
                    <img
                      src={c.img}
                      alt={c.n}
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                      onError={(e) => { e.currentTarget.src = "/images/hero.jpg"; }}
                    />
                  </div>
                  <div className="text-white text-xs">{c.n}</div>
                  <div className="text-[10px] text-[#a89878]">{c.s}</div>
                </a>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5 relative overflow-hidden">
          <h4 className="text-[#f4d089] tracking-[0.14em] text-xs font-semibold">{c?.franchise_heading || "FRANCHISE OPPORTUNITY"}</h4>
          <p className="text-white text-sm mt-3 leading-snug">{c?.franchise_subtitle || "Be a part of India's fastest growing private screening brand."}</p>
          <div className="text-2xl font-display text-[#f4d089] mt-3">{c?.franchise_amount || "₹5-10L"}<span className="text-sm text-[#bfb393]"> {c?.franchise_amount_suffix || "Monthly Potential*"}</span></div>
          <GoldButton onClick={() => (window.location.href = c?.franchise_btn_url || "/franchise")} data-testid="explore-franchise-btn" className="mt-4 text-xs">{c?.franchise_btn_label || "EXPLORE FRANCHISE"}</GoldButton>
        </Card>
        <Card className="p-5">
          <h4 className="text-[#f4d089] tracking-[0.14em] text-xs font-semibold">{c?.list_space_heading || "LIST YOUR SPACE"}</h4>
          <p className="text-white text-sm mt-3 leading-snug">{c?.list_space_title || "Earn with The CineVibe"}</p>
          <p className="text-xs text-[#bfb393] mt-2">{c?.list_space_subtitle || "List your property & get bookings from premium customers."}</p>
          <GoldButton onClick={() => (window.location.href = c?.list_space_btn_url || "/list-property")} data-testid="list-property-btn" className="mt-4 text-xs">{c?.list_space_btn_label || "LIST YOUR PROPERTY"}</GoldButton>
        </Card>
      </div>
    </Section>
  );
};

const FaqRow = ({ q, a, open, onToggle }) => (
  <div
    className={`border ${open ? "border-[#d4a574]/60 bg-[#1a0f06]/60" : "border-[#3a2a14]/80"} rounded-md px-4 py-3 cursor-pointer hover:border-[#d4a574]/40 transition`}
    onClick={onToggle}
    data-testid={`faq-row-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#d8cdb3]">{q}</span>
      <ChevronDown size={16} className={`text-[#a89878] transition-transform ${open ? "rotate-180 text-[#f4d089]" : ""}`} />
    </div>
    {open && (
      <div className="mt-3 text-[12.5px] leading-relaxed text-[#bfb393]" data-testid="faq-answer">
        {a}
      </div>
    )}
  </div>
);

const FaqAndSocial = () => {
  const [c, setC] = useState(null);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    axios.get(`${HOME_API}/content/home`).then((r) => setC(r.data)).catch(() => setC({}));
  }, []);

  const defaultFaqs = [
    { q: "How does the booking process work?", a: "Pick your city & outlet, choose a date, time slot and number of guests, then add any decor or food add-ons. Pay a small advance (₹750) or the full amount online — you'll receive an instant booking confirmation on WhatsApp and email." },
    { q: "Can I bring my own food?", a: "Outside food and beverages aren't allowed. Our in-house café (run on Billing Software) offers a curated menu of popcorn, nachos, burgers, mocktails and more — delivered fresh inside your private room. Cakes from licensed bakeries are allowed for celebrations." },
    { q: "What is the cancellation policy?", a: "Cancellations made more than 48 hours before the slot get a 100% refund of the advance. Between 24–48 hours: 50% refund. Within 24 hours of the slot: the advance is non-refundable, but you can reschedule once for free (subject to availability)." },
    { q: "Is there a minimum number of guests?", a: "No minimum — the room is privately yours. Each room has a maximum capacity (typically 6, 10 or 25 guests depending on the outlet) and pricing is per room, not per person, so smaller groups pay the same as larger ones." },
    { q: "How is billing done for food & screening?", a: "You receive two separate invoices for full transparency: (1) Screening charges billed by The CineVibe at 18% GST, and (2) Food & beverages billed via the café (Billing Software) at 5% GST. Both are emailed to you after the experience." },
    { q: "Can I customize the decoration?", a: "Yes! Choose from curated decor packages (birthday, anniversary, proposal, baby shower, bridal, etc.) or request a custom theme. Add-ons like LED name boards, fog effects, rose petals and photographers can be selected during booking." },
  ];

  const faqs = c?.faqs?.length ? c.faqs : defaultFaqs;
  const half = Math.ceil(faqs.length / 2);
  const left = faqs.slice(0, half);
  const right = faqs.slice(half);

  const toggle = (k) => setOpenKey(openKey === k ? null : k);

  return (
    <Section className="pt-16">
      <div id="faqs" className="scroll-mt-24">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#f4d089] tracking-[0.16em] text-sm font-semibold">
            {c?.faq_heading || "FREQUENTLY ASKED QUESTIONS"}
          </h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-3">
            {left.map((item, idx) => {
              const k = `L${idx}`;
              return <FaqRow key={k} q={item.q} a={item.a} open={openKey === k} onToggle={() => toggle(k)} />;
            })}
          </div>
          <div className="space-y-3">
            {right.map((item, idx) => {
              const k = `R${idx}`;
              return <FaqRow key={k} q={item.q} a={item.a} open={openKey === k} onToggle={() => toggle(k)} />;
            })}
          </div>
        </div>
      </Card>
      </div>
    </Section>
  );
};

const TrustStrip = () => {
  const items = [
    { i: <Zap size={18} />, t: "Instant Booking", s: "Confirm in just a few clicks" },
    { i: <ShieldCheck size={18} />, t: "Secure Payments", s: "100% safe & secure" },
    { i: <BadgeCheck size={18} />, t: "Best Price Guarantee", s: "No hidden charges" },
  ];
  return (
    <Section className="pt-12 pb-12">
      <Card className="p-5">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {items.map((it) => (
            <div key={it.t} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md border border-[#d4a574]/30 flex items-center justify-center text-[#f4d089] shrink-0">{it.i}</div>
              <div><div className="text-white text-sm">{it.t}</div><div className="text-[11px] text-[#a89878]">{it.s}</div></div>
            </div>
          ))}
        </div>
      </Card>
    </Section>
  );
};

const Footer = () => (
  <footer className="border-t border-[#3a2a14]/60 bg-[#070402] pt-12 pb-6">
    <Section>
      <div className="grid lg:grid-cols-[1.2fr_1fr_1fr_1fr_1.4fr] gap-8">
        <div>
          <Logo />
          <p className="text-[#a89878] text-sm mt-4">Your movie. Your space. Your vibe.</p>
          <div className="flex items-center gap-3 mt-4">
            {[<Instagram key="ig" size={16} />, <Facebook key="fb" size={16} />, <Youtube key="yt" size={16} />, <MessageCircle key="wa" size={16} />].map((I, i) => (
              <button key={i} className="w-8 h-8 rounded-full border border-[#d4a574]/30 flex items-center justify-center text-[#f4d089]">{I}</button>
            ))}
          </div>
        </div>
        <div>
          <h5 className="text-[#f4d089] tracking-[0.14em] text-xs font-semibold mb-3">QUICK LINKS</h5>
          {[
            { l: "Home", h: "/" },
            { l: "Book Screening", h: "/book" },
            { l: "Order Food", h: "/food" },
            { l: "Experiences", h: "/experiences" },
            { l: "Locations", h: "/locations" },
            { l: "About Us", h: "/about" },
            { l: "Contact Us", h: "/contact" },
          ].map((x) => (
            <a key={x.l} href={x.h} className="block text-sm text-[#bfb393] hover:text-[#f4d089] py-1">{x.l}</a>
          ))}
        </div>
        <div>
          <h5 className="text-[#f4d089] tracking-[0.14em] text-xs font-semibold mb-3">CUSTOMER SUPPORT</h5>
          {[
            { l: "Help Center", h: "/contact", ext: false },
            {
              l: "FAQs",
              h: "/#faqs",
              ext: false,
              click: (e) => {
                const el = document.getElementById("faqs");
                if (el) {
                  // Same page → smooth scroll
                  e.preventDefault();
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  history.replaceState(null, "", "/#faqs");
                }
                // Else: fall through to default navigation → / then jump to #faqs
              },
            },
            { l: "Cancellation Policy", h: "https://docs.google.com/document/d/1NIzS2tfGSpd1pCu8WWyX-yZzKqWWm5M0WMflUZFzYRk/edit?usp=sharing", ext: true },
            { l: "Terms & Conditions", h: "https://docs.google.com/document/d/17XLiSDU4FvBzxCyOAATmCuRdjsE1YeCXt3SpAO8kVWY/edit?usp=sharing", ext: true },
            { l: "Privacy Policy", h: "https://docs.google.com/document/d/1jNhoMT4Sek6ipVIyPSQ_ZbMEP0r90OkRugc5pns_21g/edit?pli=1&tab=t.0", ext: true },
            { l: "Refund Policy", h: "https://docs.google.com/document/d/1uWi84vwWi3X7i4HM-c3ZCc7jrWIU-QV_pR5m6KzyYew/edit?usp=sharing", ext: true },
          ].map((x) => (
            <a
              key={x.l}
              href={x.h}
              onClick={x.click}
              target={x.ext ? "_blank" : undefined}
              rel={x.ext ? "noopener noreferrer" : undefined}
              className="block text-sm text-[#bfb393] hover:text-[#f4d089] py-1"
            >
              {x.l}
            </a>
          ))}
        </div>
        <div>
          <h5 className="text-[#f4d089] tracking-[0.14em] text-xs font-semibold mb-3">FOR PARTNERS</h5>
          <a href="/franchise" className="block text-sm text-[#bfb393] hover:text-[#f4d089] py-1">Franchise Opportunity</a>
          <a href="/list-property" className="block text-sm text-[#bfb393] hover:text-[#f4d089] py-1">List Your Property</a>
          <a href="/admin" className="block text-sm text-[#bfb393] hover:text-[#f4d089] py-1" data-testid="footer-admin-link">Admin Login</a>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-[#143c20] to-[#0a1f10] border border-[#7ad99c]/30 p-5">
          <h5 className="text-[#f4d089] text-sm font-semibold">PLAN YOUR EXPERIENCE ON WHATSAPP</h5>
          <p className="text-xs text-[#bfb393] mt-2">Get instant assistance and custom recommendations.</p>
          <button data-testid="footer-whatsapp-cta" onClick={() => openWhatsApp(DEFAULT_GREETING)} className="mt-3 bg-[#25D366] text-white text-sm rounded-md px-4 py-2 flex items-center gap-2 hover:bg-[#20bf5a] transition">
            <MessageCircle size={16} /> Chat on WhatsApp
          </button>
        </div>
      </div>
      <div className="mt-10 pt-5 border-t border-[#3a2a14]/60 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-xs text-[#a89878] leading-relaxed">
          © 2026 The CineVibe. All Rights Reserved.<br />
          <span className="text-[#bfb393]">BnBBuddy Property Management Services Pvt Ltd</span>
          {" · "}
          <span className="text-[#bfb393]">GSTIN: 07AALCB5876H1Z8</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#a89878]">
          {["VISA", "Mastercard", "RuPay", "UPI", "Razorpay"].map((p) => (
            <span key={p} className="border border-[#3a2a14] rounded px-2 py-1">{p}</span>
          ))}
        </div>
      </div>
    </Section>
  </footer>
);

const FloatingWA = () => (
  <button
    data-testid="float-whatsapp"
    onClick={() => openWhatsApp(DEFAULT_GREETING)}
    aria-label="Chat with CineVibe on WhatsApp"
    className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-[0_8px_30px_rgba(37,211,102,0.5)] hover:scale-105 transition"
  >
    <MessageCircle className="text-white" />
  </button>
);

export default function Home() {
  return (
    <div className="min-h-screen bg-[#0a0604] text-white" data-testid="cinevibe-home">
      <Header />
      <StatusStrip />
      <Hero />
      <BookingBar />
      <HowItWorks />
      <Experiences />
      <AddonsAndFood />
      <StatsAndBilling />
      <LocationsRow />
      <FaqAndSocial />
      <TrustStrip />
      <Footer />
      <FloatingWA />
    </div>
  );
}

export { Footer };
